import React, { useMemo, useState } from 'react';
import { Button, Flex, Form, Input, Popconfirm } from 'antd';
import { FormContainer, READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import AdminFieldInput from './AdminFieldInput';
import AdminNewFieldModal from './AdminNewFieldModal';
import { EFieldType } from '../../../../domain/enums/admin/AdminFieldType';

const EXCLUDED_TAB_META_KEYS = new Set(['label', 'key', 'index', 'custom_field_component']);

const formatLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface AdminGeneratedTabProps {
  tabKey: string;
  tabValue: Record<string, unknown>;
  isReadOnly: boolean;
  onRemove?: (path: (string | number)[]) => void;
  onAdd?: (path: (string | number)[], value: unknown) => void;
  onRemoveTab?: () => void;
}

const AdminGeneratedTab: React.FC<AdminGeneratedTabProps> = ({ tabKey, tabValue, isReadOnly, onRemove, onAdd, onRemoveTab }) => {
  const [addTarget, setAddTarget] = useState<{ path: (string | number)[]; keys: string[] } | null>(null);

  // Sort alphabetically: Firestore returns object keys in arbitrary order, so without
  // this the field order would shuffle on every refresh.
  const entries = Object.entries(tabValue)
    .filter(([k]) => !EXCLUDED_TAB_META_KEYS.has(k))
    .sort(([a], [b]) => a.localeCompare(b));
  if (onAdd) entries.unshift(["tabs_management", undefined]); // Tab management row (add field + delete page), shown first

  const notAllowedKeys = useMemo(() => entries.map(([key]) => key), [entries]);

  const requestAdd = onAdd
    ? (path: (string | number)[], existingKeys: string[]) => setAddTarget({ path, keys: existingKeys })
    : undefined;

  const fieldLabel = (key: string, path: (string | number)[]): React.ReactNode =>
    !onRemove ? formatLabel(key) : (
      <Flex align="center" gap={8}>
        <span>{formatLabel(key)}</span>
        <Popconfirm
          title="Supprimer ce champ ?"
          okText="Supprimer"
          cancelText="Annuler"
          okButtonProps={{ danger: true }}
          onConfirm={() => onRemove(path)}
        >
          <DeleteOutlined style={{ color: 'var(--danger-color)' }} />
        </Popconfirm>
      </Flex>
    );

  return (
    <FormContainer>
      {Array.from(EXCLUDED_TAB_META_KEYS).map((metaKey) =>
        tabValue[metaKey] !== undefined ? (
          <Form.Item key={metaKey} name={[tabKey, metaKey]} hidden>
            <Input readOnly={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
          </Form.Item>
        ) : null
      )}
      {entries.map(([fieldKey, fieldValue]) => {
          const path = [tabKey, fieldKey];

          if (fieldKey === 'tabs_management') {
            return (
              <Form.Item key={fieldKey} label={"Gestion de la page"}>
                <Flex gap={8} align="center" wrap="wrap" style={{ width: '100%', border: '3px solid var(--background-color)', padding: '8px', borderRadius: '8px' }}>
                  <Button icon={<PlusOutlined />} type="primary" onClick={() => requestAdd?.([tabKey], notAllowedKeys)}>
                    Ajouter un champ
                  </Button>
                  {onRemoveTab && (
                    <Popconfirm
                      title="Supprimer cette page ?"
                      okText="Supprimer"
                      cancelText="Annuler"
                      okButtonProps={{ danger: true }}
                      onConfirm={onRemoveTab}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Supprimer cette page
                      </Button>
                    </Popconfirm>
                  )}
                </Flex>
              </Form.Item>
            );
          }

          const label = fieldLabel(fieldKey, path);

          if (typeof fieldValue === 'boolean') {
            return <AdminFieldInput key={fieldKey} type={EFieldType.BOOLEAN} namePath={path} label={label} extra={fieldKey} isReadOnly={isReadOnly} />;
          }

          if (typeof fieldValue === 'number') {
            return <AdminFieldInput key={fieldKey} type={EFieldType.NUMBER} namePath={path} label={label} extra={fieldKey} isReadOnly={isReadOnly} />;
          }

          if (Array.isArray(fieldValue)) {
            return <AdminFieldInput key={fieldKey} type={EFieldType.ARRAY} namePath={path} label={label} extra={fieldKey} value={fieldValue} isReadOnly={isReadOnly} />;
          }

          if (typeof fieldValue === 'object' && fieldValue !== null) {
            return (
              <AdminFieldInput
                key={fieldKey}
                type={fieldValue && 'custom_field_component' in fieldValue ? EFieldType.CUSTOM : EFieldType.OBJECT}
                namePath={path}
                label={label}
                extra={fieldKey}
                value={fieldValue}
                isReadOnly={isReadOnly}
                onRemove={onRemove}
                onAdd={requestAdd}
                onSet={onAdd}
              />
            );
          }

          return <AdminFieldInput key={fieldKey} type={EFieldType.STRING} namePath={path} label={label} extra={fieldKey} isReadOnly={isReadOnly} />;
        })}

      <AdminNewFieldModal
        open={addTarget !== null}
        onCancel={() => setAddTarget(null)}
        notAllowedKeys={addTarget?.keys ?? []}
        onAdded={(key, value) => {
          if (addTarget) onAdd?.([...addTarget.path, key], value);
          setAddTarget(null);
        }}
      />
    </FormContainer>
  );
};

export default AdminGeneratedTab;
